// Wordle Game with daily word logic and localStorage persistence
import { useEffect, useRef, useState } from "react";
import { Row, RowState } from "./Row";
import dictionary from "./dictionary.json";
import { Clue, clue, describeClue, violation } from "./clue";
import { Keyboard } from "./Keyboard";
import targetList from "./targets.json";
import {
  describeSeed,
  dictionarySet,
  Difficulty,
  gameName,
  pick,
  resetRng,
  seed,
  speak,
  urlParam,
} from "./util";
import { decode, encode } from "./base64";

enum GameState {
  Playing,
  Won,
  Lost,
}

interface GameProps {
  maxGuesses: number;
  hidden: boolean;
  difficulty: Difficulty;
  colorBlind: boolean;
  keyboardLayout: string;
}

const targets = targetList.slice(0, targetList.indexOf("murky") + 1); // Words no rarer than this one
const minLength = 4;
const defaultLength = 5;
const maxLength = 11;
const limitLength = (n: number) =>
  n >= minLength && n <= maxLength ? n : defaultLength;

function getTodayKey(): string {
  return new Date().toISOString().split("T")[0];
}

function getDailyTarget(wordLength: number): string {
  const userID = getQueryVariable("userid") || "default";
  const key = `wordl-${getTodayKey()}-${userID}-target`;
  const saved = localStorage.getItem(key);
  if (saved) return saved;

  const eligible = targets.filter((word) => word.length === wordLength);

  // Create a hash from the combined date and userID
  const seedStr = `${getTodayKey()}-${userID}`;
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = (hash << 5) - hash + seedStr.charCodeAt(i);
    hash |= 0; // Force 32bit integer
  }

  const index = Math.abs(hash) % eligible.length;
  const targetWord = eligible[index];

  localStorage.setItem(key, targetWord);
  return targetWord;
}


function getQueryVariable(variable: string) {
  const query = window.location.search.substring(1);
  const vars = query.split("&");
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split("=");
    if (pair[0] === variable) return pair[1];
  }
  return "";
}

function WordlResultPost(userid: string, guessedword: string, guesses: number, emojiview: string, teamid: number, challengecode: string, giveup: number) {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      UserID: getQueryVariable('userid'),
      GuessedWord: guessedword,
      Guesses: guesses,
      EmojiView: emojiview,
      TeamID: 0,
      ChallengeCode: getQueryVariable('challengecode'),
      GiveUp: giveup
    })
  };
  fetch('https://sswindowslegacycoreapi.azurewebsites.net/wordlEntry', requestOptions);
  //fetch('https://www.ragnarstudios.com/ssapi/wordlEntry', requestOptions);
}

function Game(props: GameProps) {
  const [gameState, setGameState] = useState(() => {
    const saved = localStorage.getItem(`wordl-${getTodayKey()}-${getQueryVariable("userid") || "default"}-gamestate`);
    return saved ? Number(saved) : GameState.Playing;
  });

  const [guesses, setGuesses] = useState<string[]>(() => {
    const saved = localStorage.getItem(`wordl-${getTodayKey()}-${getQueryVariable("userid") || "default"}-guesses`);
    return saved ? JSON.parse(saved) : [];
  });

  const [currentGuess, setCurrentGuess] = useState<string>("");
  const [challenge, setChallenge] = useState<string>("");
  const [wordLength, setWordLength] = useState(defaultLength);
  const [gameNumber, setGameNumber] = useState(1);
  const [target, setTarget] = useState(() => getDailyTarget(wordLength));
  const [hint, setHint] = useState<string>("Welcome to Wordl!");

  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    localStorage.setItem(`wordl-${getTodayKey()}-${getQueryVariable("userid") || "default"}-guesses`, JSON.stringify(guesses));
  }, [guesses]);

  useEffect(() => {
    localStorage.setItem(`wordl-${getTodayKey()}-${getQueryVariable("userid") || "default"}-gamestate`, gameState.toString());
  }, [gameState]);

  const onKey = (key: string) => {
    if (gameState !== GameState.Playing) return;
    if (guesses.length === props.maxGuesses) return;
    if (/^[a-z]$/i.test(key)) {
      setCurrentGuess((guess) =>
        (guess + key.toLowerCase()).slice(0, wordLength)
      );
      tableRef.current?.focus();
      setHint("");
    } else if (key === "Backspace") {
      setCurrentGuess((guess) => guess.slice(0, -1));
      setHint("");
    } else if (key === "Enter") {
      if (currentGuess.length !== wordLength) {
        setHint("Too short");
        return;
      }
      if (!dictionary.includes(currentGuess)) {
        setHint("Not a valid word");
        return;
      }
      for (const g of guesses) {
        const c = clue(g, target);
        const feedback = violation(props.difficulty, c, currentGuess);
        if (feedback) {
          setHint(feedback);
          return;
        }
      }
      const newGuesses = guesses.concat([currentGuess]);
      setGuesses(newGuesses);
      setCurrentGuess("");

      const emoji = props.colorBlind ? ["⬛", "🟦", "🟧"] : ["⬛", "🟨", "🟩"];
      const resultcopy = `${gameName} ${newGuesses.length}/${props.maxGuesses}\n` +
        newGuesses.map((guess) => clue(guess, target).map((c) => emoji[c.clue ?? 0]).join(""))
          .join("\n");

      if (currentGuess === target) {
        setHint(`You won! The answer was ${target.toUpperCase()}.`);
        setGameState(GameState.Won);
        WordlResultPost('', target.toUpperCase(), newGuesses.length, resultcopy, 0, '', 0);
      } else if (newGuesses.length === props.maxGuesses) {
        setHint(`You lost! The answer was ${target.toUpperCase()}.`);
        setGameState(GameState.Lost);
        WordlResultPost('', target.toUpperCase(), newGuesses.length, resultcopy, 0, '', 0);
      } else {
        setHint("");
        speak(describeClue(clue(currentGuess, target)));
      }
    }
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        onKey(e.key);
      }
      if (e.key === "Backspace") {
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [currentGuess, gameState, guesses]);

  let letterInfo = new Map<string, Clue>();
  const tableRows = Array(props.maxGuesses)
    .fill(undefined)
    .map((_, i) => {
      const guess = [...guesses, currentGuess][i] ?? "";
      const cluedLetters = clue(guess, target);
      const lockedIn = i < guesses.length;
      if (lockedIn) {
        for (const { clue, letter } of cluedLetters) {
          if (clue === undefined) break;
          const old = letterInfo.get(letter);
          if (old === undefined || clue > old) {
            letterInfo.set(letter, clue);
          }
        }
      }
      return (
        <Row
          key={i}
          wordLength={wordLength}
          rowState={
            lockedIn
              ? RowState.LockedIn
              : i === guesses.length
              ? RowState.Editing
              : RowState.Pending
          }
          cluedLetters={cluedLetters}
        />
      );
    });

  return (
    <div className="Game" style={{ display: props.hidden ? "none" : "block" }}>
      <label htmlFor="wordLength">Each person gets one unique word a day.<br /><br />You only earn points for 1 Wordl a day.</label><br />
      <small>Your progress is saved until tomorrow</small>
      <br /><br />
      <div className="Game-options">
        <label htmlFor="wordLength">Letters:</label>
        <input
          type="range"
          min={minLength}
          max={maxLength}
          id="wordLength"
          disabled
          value={wordLength}
          onChange={() => {}}
        ></input>
      </div>
      <table
        className="Game-rows"
        tabIndex={0}
        aria-label="Table of guesses"
        ref={tableRef}
      >
        <tbody>{tableRows}</tbody>
      </table>
      <p
        role="alert"
        style={{
          userSelect: /https?:/.test(hint) ? "text" : "none",
          whiteSpace: "pre-wrap",
        }}
      >
        {hint || `\u00a0`}
      </p>
      <Keyboard
        layout={props.keyboardLayout}
        letterInfo={letterInfo}
        onKey={onKey}
      />
    </div>
  );
}

export default Game;
